import React from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import {
  Fingerprint,
  ArrowRight,
  ArrowLeft,
  Sparkles,
  Check,
  Tag,
  Clock,
} from 'lucide-react'
import SEOHead from '../components/SEOHead'
import { sendMetaEvent } from '../utils/metaCapi'

const packages = [
  {
    slug: 'payrollx',
    name: 'PayrollX',
    tagline: 'Attendance, Payroll & Violations',
    description:
      'Connect fingerprint devices, process punch logs in real-time, calculate deductions and overtime, and generate payroll master sheets — all from one dashboard.',
    icon: Fingerprint,
    badge: 'New',
    startingPrice: 'EGP 15,000',
    highlight: true,
    features: [
      'Real-time attendance feed',
      'Violation tracking with escalation',
      'Automated deductions & overtime',
      'Payroll master sheet generation',
    ],
  },
  {
    slug: 'coming-soon-1',
    name: 'HireX',
    tagline: 'AI Candidate Screening & Exams',
    description:
      'AI-powered candidate screening, automated exams, skill assessments, and interview scheduling — streamline your hiring pipeline.',
    icon: Sparkles,
    badge: 'Soon',
    startingPrice: '—',
    highlight: false,
    features: ['AI resume screening', 'Automated skill exams', 'Interview scheduling', 'Candidate scoring'],
    comingSoon: true,
  },
  {
    slug: 'coming-soon-2',
    name: 'Coming Soon',
    tagline: 'Another product on the way',
    description:
      'We are working on more packaged products. This slot is reserved for the next one. Stay tuned.',
    icon: Sparkles,
    badge: 'Soon',
    startingPrice: '—',
    highlight: false,
    features: ['To be announced', 'To be announced', 'To be announced', 'To be announced'],
    comingSoon: true,
  },
]

export default function Packages() {
  const { t } = useTranslation()

  const packages = [
    {
      slug: 'payrollx',
      name: 'PayrollX',
      tagline: t('packages.payrollxTagline', 'Attendance, Payroll & Violations'),
      description: t('packages.payrollxDesc', 'Connect fingerprint devices, process punch logs in real-time, calculate deductions and overtime, and generate payroll master sheets — all from one dashboard.'),
      icon: Fingerprint,
      badge: t('packages.payrollxBadge', 'New'),
      startingPrice: 'EGP 15,000',
      highlight: true,
      features: t('packages.payrollxFeatures', { returnObjects: true, defaultValue: ['Real-time attendance feed', 'Violation tracking with escalation', 'Automated deductions & overtime', 'Payroll master sheet generation'] }),
    },
    {
      slug: 'coming-soon-1',
      name: 'HireX',
      tagline: t('packages.hirexTagline', 'AI Candidate Screening & Exams'),
      description: t('packages.hirexDesc', 'AI-powered candidate screening, automated exams, skill assessments, and interview scheduling — streamline your hiring pipeline.'),
      icon: Sparkles,
      badge: t('packages.hirexBadge', 'Soon'),
      startingPrice: '—',
      highlight: false,
      features: t('packages.hirexFeatures', { returnObjects: true, defaultValue: ['AI resume screening', 'Automated skill exams', 'Interview scheduling', 'Candidate scoring'] }),
      comingSoon: true,
    },
    {
      slug: 'coming-soon-2',
      name: t('packages.comingSoonName', 'Coming Soon'),
      tagline: t('packages.comingSoonTagline', 'Another product on the way'),
      description: t('packages.comingSoonDesc', 'We are working on more packaged products. This slot is reserved for the next one. Stay tuned.'),
      icon: Sparkles,
      badge: t('packages.comingSoonBadge', 'Soon'),
      startingPrice: '—',
      highlight: false,
      features: t('packages.comingSoonFeatures', { returnObjects: true, defaultValue: ['To be announced', 'To be announced', 'To be announced', 'To be announced'] }),
      comingSoon: true,
    },
  ]

  return (
    <main className="pt-20">
      <SEOHead
        title="Packages — Products by Nodatx"
        description="Explore packaged products by Nodatx. Build your own package and send it straight to WhatsApp."
        canonical="https://nodatx.com/packages"
      />

      {/* Hero */}
      <section className="relative py-16 sm:py-24 px-4 sm:px-6 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute inset-0 bg-[#0a0a0f]" />
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-purple-600/20 rounded-full blur-[150px]" />
        </div>

        <div className="container-custom relative z-10">
          {/* Top-left back link */}
          <div className="flex justify-start mb-8">
              <Link
                to="/"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/5 border border-white/10 rounded-full text-xs font-medium text-gray-300 hover:text-purple-400 hover:border-purple-500/50 transition-all duration-200 active:scale-95"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                {t('packages.backTo', 'Nodatx')}
              </Link>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto text-center"
          >
            <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-purple-500/10 border border-purple-500/30 rounded-full text-purple-400 text-xs font-semibold uppercase tracking-wider mb-6">
              <Tag className="w-3.5 h-3.5" />
              {t('packages.badge', 'Packages')}
            </span>

            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight leading-[1.1] text-white">
              {t('packages.heading', 'Our')} <span className="text-purple-400">{t('packages.headingHighlight', 'Packages')}</span>
            </h1>

            <p className="mt-6 text-base sm:text-lg text-gray-400 max-w-2xl mx-auto leading-relaxed">
              {t('packages.description', "Packaged products by Nodatx. Each product is configurable — choose what you need, see your price live, and send it to us on WhatsApp.")}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Package cards grid */}
      <section className="pb-20 sm:pb-28 px-4 sm:px-6 bg-[#0a0a0f] relative">
        <div className="container-custom relative z-10">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 max-w-6xl mx-auto">
            {packages.map((pkg, i) => (
              <motion.div
                key={pkg.slug}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-50px' }}
                transition={{ delay: i * 0.1, duration: 0.45 }}
                className="h-full"
              >
                {pkg.comingSoon ? (
                  <div className="relative h-full bg-gradient-to-br from-[#15151f] to-[#0c0c14] border border-white/10 rounded-2xl overflow-hidden flex flex-col">
                    {/* Image placeholder */}
                    <div className="relative aspect-[16/10] sm:aspect-[16/9] bg-[#0d0d18] border-b border-white/5 overflow-hidden flex items-center justify-center">
                      <img
                        src="https://i.ibb.co/gFwf9Z16/under-construction.png"
                        alt="Coming Soon"
                        className="w-full h-full object-cover"
                      />
                    </div>

                    <div className="p-4 sm:p-6 flex-1 flex flex-col">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-white/5 text-gray-400 border border-white/10">
                          {pkg.badge}
                        </span>
                      </div>
                      <h3 className="text-base sm:text-xl font-bold text-white mb-1">{pkg.name}</h3>
                      <p className="text-purple-400/70 text-[11px] sm:text-xs font-medium mb-3">{pkg.tagline}</p>
                      <p className="text-gray-500 text-xs sm:text-sm leading-relaxed mb-4 sm:mb-5 flex-1">{pkg.description}</p>

                      <div className="pt-3 sm:pt-4 border-t border-white/5 flex items-center justify-between">
                        <span className="text-[11px] sm:text-xs text-gray-500">{t('packages.startingFrom', 'Starting from')}</span>
                        <span className="text-sm sm:text-base font-bold text-gray-500">{pkg.startingPrice}</span>
                      </div>

                      <div className="mt-3 sm:mt-4 w-full px-4 py-2.5 sm:py-3 rounded-xl bg-white/[0.02] border border-white/5 text-gray-500 text-xs sm:text-sm font-medium text-center cursor-not-allowed">
                        {t('packages.comingSoon', 'Coming Soon')}
                      </div>
                    </div>
                  </div>
                ) : (
                  <Link
                    to={`/packages/${pkg.slug}`}
                    onClick={() =>
                      sendMetaEvent('SectionClick', {
                        content_name: pkg.name,
                        section_id: 'packages',
                        page_path: '/packages',
                      })
                    }
                    className="group relative block h-full bg-gradient-to-br from-[#15151f] to-[#0c0c14] border rounded-2xl overflow-hidden transition-all duration-300 hover:border-purple-500/50 hover:shadow-[0_0_60px_-20px_rgba(139,92,246,0.4)] hover:-translate-y-1"
                  >
                    {/* Image placeholder */}
                    <div className="relative aspect-[16/10] sm:aspect-[16/9] bg-[#0d0d18] border-b border-white/5 overflow-hidden">
                      <img
                        src="https://i.ibb.co/zVgngVNT/Nodatx-1920-x-1080-px-1.png"
                        alt={pkg.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>

                    <div className="p-4 sm:p-6 flex flex-col">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-purple-500/15 text-purple-400 border border-purple-500/20">
                          {pkg.badge}
                        </span>
                      </div>
                      <h3 className="text-base sm:text-xl font-bold text-white mb-1">{pkg.name}</h3>
                      <p className="text-purple-400 text-[11px] sm:text-xs font-medium mb-3">{pkg.tagline}</p>
                      <p className="text-gray-400 text-xs sm:text-sm leading-relaxed mb-4 sm:mb-5">{pkg.description}</p>

                      <ul className="space-y-1.5 sm:space-y-2 mb-4 sm:mb-5">
                        {pkg.features.map((f, j) => (
                          <li key={j} className="flex items-start gap-2 text-[11px] sm:text-xs text-gray-300">
                            <Check className="w-3.5 h-3.5 text-purple-400 flex-shrink-0 mt-0.5" />
                            <span>{f}</span>
                          </li>
                        ))}
                      </ul>

                      <div className="mt-auto pt-3 sm:pt-4 border-t border-white/10 flex items-center justify-between">
                        <div>
                          <span className="text-[11px] sm:text-xs text-gray-500 block">{t('packages.startingFrom', 'Starting from')}</span>
                          <span className="text-base sm:text-lg font-bold text-white">{pkg.startingPrice}</span>
                        </div>
                        <span className="inline-flex items-center justify-center gap-1.5 px-3 sm:px-4 py-2 sm:py-2.5 bg-purple-600 text-white text-xs sm:text-sm font-semibold rounded-xl transition-all duration-200 group-hover:scale-105 group-hover:shadow-[0_0_30px_-10px_rgba(139,92,246,0.5)]">
                          {t('packages.configure', 'Configure')}
                          <ArrowRight className="w-3.5 h-3.5 sm:w-4 sm:h-4 group-hover:translate-x-1 transition-transform" />
                        </span>
                      </div>
                    </div>
                  </Link>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </main>
  )
}
